import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePlanFlags } from "@/contexts/PlanFlagsContext";
import { useAddons } from "@/contexts/AddonsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle, ArrowRight, CalendarBlank, Prohibit, Sparkle, Star,
  Rocket, Briefcase, SealCheck,
} from "@phosphor-icons/react";
import EnterpriseWorkforceAddonCard from "@/components/EnterpriseWorkforceAddonCard";

import { devLog } from "@/lib/devLog";
/**
 * Owner / Admin subscription management.
 *
 * Design (Iter-44):
 *   1. Header + current-plan hero (shows renewal, cycle, add-on state).
 *   2. Prompt banner when workspace is on Trial / expired / no active sub.
 *   3. Plan comparison — always visible with the current plan clearly
 *      badged. Monthly ↔ Annual toggle drives per-card pricing (annual
 *      price = monthly × 12 × 0.85, i.e. 15% off — matches the addon
 *      pattern).
 *   4. Enterprise Workforce Suite add-on section (delegated to
 *      <EnterpriseWorkforceAddonCard/>).
 *   5. Cancel action (only when an active sub exists) collapsed into a
 *      subordinate footer card so it doesn't dominate the page.
 *
 * The P0 blocker the earlier UI had — "when an active sub exists we only
 * showed the cancel card and hid the plan comparison" — is fixed by
 * ALWAYS rendering the comparison grid. Switching plans hits the same
 * Razorpay-checkout flow.
 */
const TIER_ORDER = ["starter", "professional", "growth"];
const HIGHLIGHTED = "professional";
const ANNUAL_DISCOUNT_PCT = 15; // industry-standard for annual billing

export default function SubscriptionPage() {
  const { user } = useAuth();
  const { plan, meta, refresh: refreshPlan } = usePlanFlags();
  const { hasEnterpriseWorkforce } = useAddons();
  const navigate = useNavigate();
  const [presets, setPresets] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerTier, setPickerTier] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ contact_name: "", contact_phone: "", company_size: "", message: "" });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/platform/plan-presets/public").catch(() => api.get("/plan/public/pricing")),
        api.get("/subscriptions/current"),
      ]);
      setPresets(a.data.presets || {});
      setSubscription(b.data.subscription);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Post-Razorpay-checkout reconcile poll. Iter-42 kept this intact.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "razorpay") return;
    let stop = false;
    (async () => {
      try {
        const r = await api.post("/subscriptions/reconcile");
        if (r.data.subscription) setSubscription(r.data.subscription);
        await refreshPlan();
        toast.success("Checking payment status…");
      } catch (e) { devLog("initial reconcile failed", e); }
    })();
    const tick = async () => {
      if (stop) return;
      try { await api.post("/subscriptions/reconcile"); }
      catch (e) { devLog("polling reconcile failed", e); }
      await load();
      await refreshPlan();
      setTimeout(tick, 3000);
    };
    setTimeout(tick, 3000);
    setTimeout(() => { stop = true; }, 60000);
  }, []);

  const roles = user?.roles || [];
  const canManage = roles.includes("owner") || roles.includes("admin");
  const isTrial = plan?.billing_cycle === "trial";
  const daysLeft = meta?.days_remaining;
  const isExpired = typeof daysLeft === "number" && daysLeft < 0;
  const activeSub = subscription && ["active", "authenticated", "pending"].includes(subscription.status);

  // Which tier key does the workspace currently sit on? Fall back by
  // matching the tier_name label since older plan docs might not carry
  // a tier_key. This drives the "Current plan" pill on each card.
  const currentTierKey = useMemo(() => {
    if (!plan || !presets) return null;
    const t = (plan.tier_name || "").toLowerCase();
    return TIER_ORDER.find(k => k === t) || null;
  }, [plan, presets]);

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const annualPrice = (monthly) => Math.round(monthly * 12 * (1 - ANNUAL_DISCOUNT_PCT / 100));

  const startCheckout = async () => {
    if (!pickerTier) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/create", {
        tier_key: pickerTier,
        billing_cycle: cycle,
      });
      const url = data.checkout_url;
      if (!url) throw new Error("Razorpay did not return a checkout URL");
      window.location.href = url
        + (url.includes("?") ? "&" : "?")
        + "callback_url=" + encodeURIComponent(window.location.origin + "/billing/subscription?from=razorpay");
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  const doCancel = async (at_cycle_end) => {
    setLoading(true);
    try {
      await api.post("/subscriptions/cancel", { at_cycle_end });
      toast.success(at_cycle_end
        ? "Cancelled — you'll retain access until the current period ends."
        : "Cancelled immediately.");
      setCancelOpen(false);
      await load();
      await refreshPlan();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const submitEnquiry = async () => {
    if (!enquiryForm.contact_name.trim()) {
      toast.error("Please enter a contact name"); return;
    }
    setEnquirySubmitting(true);
    try {
      await api.post("/platform/enterprise-enquiry", enquiryForm);
      toast.success("Thanks — our team will reach out shortly.");
      setEnquiryOpen(false);
      setEnquiryForm({ contact_name: "", contact_phone: "", company_size: "", message: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEnquirySubmitting(false); }
  };

  const currentTierLabel = plan?.tier_name || "—";

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <div className="overline mb-1">Billing</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Subscription</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Compare plans, upgrade or switch billing cycle any time — you keep everything you&apos;ve entered.
          Cancel any time — data stays exportable.
        </p>
      </div>

      {/* Current plan hero */}
      <Card data-testid="current-plan-hero">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline flex items-center gap-1.5 mb-1">
                <Sparkle size={12} weight="duotone" /> Currently on
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-display text-3xl font-bold tracking-tight">{currentTierLabel}</span>
                {isTrial && (
                  <Badge className={isExpired ? "bg-red-100 text-red-800 border-red-200" : "bg-amber-100 text-amber-800 border-amber-200"}>
                    Trial · {isExpired ? `expired ${Math.abs(daysLeft)}d ago` : `${daysLeft ?? "—"}d left`}
                  </Badge>
                )}
                {!isTrial && plan?.monthly_price > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {inr(plan.monthly_price)} / month
                    {plan.billing_cycle === "annual" ? " · billed annually" : ""}
                    {plan.expires_on ? ` · renews ${plan.expires_on}` : ""}
                  </span>
                )}
              </div>
            </div>
            <Badge
              className={hasEnterpriseWorkforce
                ? "bg-emerald-100 text-emerald-800 border-emerald-200 gap-1"
                : "bg-muted text-muted-foreground border-border gap-1"}
              data-testid="hero-addon-badge"
            >
              <Briefcase size={11} weight="duotone" />
              {hasEnterpriseWorkforce ? "Workforce Suite active" : "Workforce Suite not active"}
            </Badge>
          </div>
          {activeSub && (
            <div className="text-xs text-muted-foreground border-t border-border pt-2 flex flex-wrap gap-4">
              <span className="flex items-center gap-1">
                <CalendarBlank size={12} weight="duotone" />
                Current period ends {subscription.current_end ? new Date(subscription.current_end).toLocaleDateString() : "—"}
              </span>
              <span>Cycle: <span className="font-medium capitalize">{subscription.billing_cycle || plan?.billing_cycle || "—"}</span></span>
              <span>Status: <span className="font-medium capitalize">{subscription.status}</span></span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscribe prompt — only shown for Trial / expired / no active sub */}
      {canManage && (isTrial || isExpired || !activeSub) && (
        <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-4 flex items-start gap-3" data-testid="subscribe-prompt">
          <div className="rounded-full bg-primary/15 p-2 mt-0.5 flex-none">
            <Rocket size={20} weight="duotone" className="text-primary" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-primary">
              {isExpired ? "Your trial has expired" : isTrial ? "You're on a free trial" : "No active subscription"}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Subscribe to a plan below to unlock the full workspace, remove usage limits and keep everything running smoothly.
            </p>
          </div>
        </div>
      )}

      {!canManage && (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4">
          Only the workspace Owner or Admin can start, switch, or cancel a subscription.
        </p>
      )}

      {/* Comparison grid — ALWAYS visible for owners/admins */}
      {canManage && presets && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-6">
            <h2 className="font-display text-xl font-bold tracking-tight">All plans</h2>
            <CycleToggle cycle={cycle} onChange={setCycle} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {presets["trial"] && (
              <TrialCard preset={presets["trial"]} isCurrent={isTrial} daysLeft={daysLeft} />
            )}
            {TIER_ORDER.map(k => {
              const t = presets[k];
              if (!t) return null;
              return (
                <TierCard
                  key={k}
                  tierKey={k}
                  tier={t}
                  cycle={cycle}
                  isCurrent={currentTierKey === k}
                  isCurrentCycle={
                    currentTierKey === k
                    && (subscription?.billing_cycle || plan?.billing_cycle) === cycle
                  }
                  isHighlighted={k === HIGHLIGHTED}
                  activeSub={activeSub}
                  currentTierKey={currentTierKey}
                  onPick={() => setPickerTier(k)}
                  inr={inr}
                  annualPrice={annualPrice}
                  isExpired={isExpired}
                />
              );
            })}
            {presets["enterprise"] && (
              <EnterpriseCard onEnquire={() => setEnquiryOpen(true)} />
            )}
          </div>
        </>
      )}

      {/* Enterprise Workforce Suite add-on */}
      <div className="pt-2">
        <h2 className="font-display text-xl font-bold tracking-tight mb-3">Add-ons</h2>
        <EnterpriseWorkforceAddonCard />
      </div>

      {/* Cancel subscription — subordinate footer card. Only cancels the
          BASE plan. The Enterprise Workforce add-on has its own cancel
          action inside the add-on card above (kept isolated to prevent
          accidentally revoking the workforce suite when the operator
          only meant to change their student-billing plan). */}
      {canManage && activeSub && (
        <Card className="border-dashed" data-testid="cancel-card">
          <CardHeader>
            <CardTitle className="text-base font-display flex items-center gap-2">
              <Prohibit size={14} /> Cancel base subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              You&apos;ll keep full access to your <strong>base plan</strong> until{" "}
              <span className="font-medium text-foreground">
                {subscription.current_end ? new Date(subscription.current_end).toLocaleDateString() : "the current period ends"}
              </span>.
              After that we&apos;ll switch you to a 3-day grace Trial so you can decide whether to renew.
              {hasEnterpriseWorkforce && (
                <span className="block mt-1 text-xs">
                  Your Enterprise Workforce Suite add-on stays untouched — manage it separately in the add-on card above.
                </span>
              )}
            </p>
            <Button variant="destructive" onClick={() => setCancelOpen(true)} data-testid="cancel-subscription-btn">
              <Prohibit size={14} className="mr-1.5" /> Cancel base subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Upgrade / switch dialog */}
      <Dialog open={!!pickerTier} onOpenChange={(o) => !o && setPickerTier(null)}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>
              {activeSub && currentTierKey && currentTierKey !== pickerTier ? "Switch plan" : "Confirm subscription"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              You&apos;ll be redirected to Razorpay to authorise the {cycle} subscription.
              You can pay by UPI, card, netbanking or wallets.
            </DialogDescription>
          </DialogHeader>
          {pickerTier && presets && (
            <div className="text-sm space-y-3">
              <div className="rounded-md border border-border bg-muted/30 p-3 flex items-baseline justify-between">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Plan</div>
                  <div className="font-semibold">{presets[pickerTier].tier_name} · {cycle}</div>
                </div>
                <div className="font-display text-xl font-bold tabular-nums">
                  {cycle === "annual"
                    ? inr(annualPrice(presets[pickerTier].monthly_price))
                    : inr(presets[pickerTier].monthly_price)}
                  <span className="text-xs text-muted-foreground font-normal font-sans">
                    {" "}/{cycle === "annual" ? "year" : "month"}
                  </span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Your plan changes take effect the moment payment succeeds — no manual step required.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerTier(null)}>Cancel</Button>
            <Button onClick={startCheckout} disabled={loading} data-testid="confirm-upgrade">
              {loading ? "Redirecting…" : "Go to Razorpay"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader><DialogTitle>Cancel subscription</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <p>How would you like to cancel?</p>
            <div className="space-y-2 text-xs">
              <p><strong>At the end of the billing cycle</strong> — you keep full access until then. Recommended.</p>
              <p><strong>Immediately</strong> — we revert you to a 3-day Trial right away. You lose any remaining paid days.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep subscription</Button>
            <Button variant="secondary" onClick={() => doCancel(true)} disabled={loading} data-testid="cancel-cycle-end">
              Cancel at cycle end
            </Button>
            <Button variant="destructive" onClick={() => doCancel(false)} disabled={loading} data-testid="cancel-immediate">
              Cancel now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enterprise "Talk to Us" enquiry dialog */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="sm:max-w-md bg-card" data-testid="enterprise-enquiry-dialog">
          <DialogHeader>
            <DialogTitle>Talk to us about Enterprise</DialogTitle>
            <DialogDescription className="text-xs">
              Custom limits, dedicated support, and SLAs — tell us about your setup and we&apos;ll be in touch within one business day.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Contact name *</label>
              <input
                className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                value={enquiryForm.contact_name}
                onChange={e => setEnquiryForm(f => ({ ...f, contact_name: e.target.value }))}
                data-testid="enterprise-enquiry-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Phone</label>
                <input
                  className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  value={enquiryForm.contact_phone}
                  onChange={e => setEnquiryForm(f => ({ ...f, contact_phone: e.target.value }))}
                  data-testid="enterprise-enquiry-phone"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Company size</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  value={enquiryForm.company_size}
                  onChange={e => setEnquiryForm(f => ({ ...f, company_size: e.target.value }))}
                  data-testid="enterprise-enquiry-size"
                >
                  <option value="">Select…</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-200">51–200</option>
                  <option value="200+">200+</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium">What are you looking for?</label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                value={enquiryForm.message}
                onChange={e => setEnquiryForm(f => ({ ...f, message: e.target.value }))}
                placeholder="e.g. Multiple centers, custom SLAs, priority support…"
                data-testid="enterprise-enquiry-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnquiryOpen(false)}>Cancel</Button>
            <Button onClick={submitEnquiry} disabled={enquirySubmitting} data-testid="enterprise-enquiry-submit">
              {enquirySubmitting ? "Sending…" : "Send enquiry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Monthly / Annual toggle
// ============================================================
function CycleToggle({ cycle, onChange }) {
  return (
    <div className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5 text-xs font-medium" data-testid="cycle-toggle">
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={`px-3.5 py-1.5 rounded-full transition-colors ${cycle === "monthly" ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="cycle-monthly"
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChange("annual")}
        className={`px-3.5 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${cycle === "annual" ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        data-testid="cycle-annual"
      >
        Annual
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
          Save {ANNUAL_DISCOUNT_PCT}%
        </span>
      </button>
    </div>
  );
}

// ============================================================
// Per-tier plan card
// ============================================================
function TierCard({ tierKey, tier, cycle, isCurrent, isCurrentCycle, isHighlighted, activeSub, currentTierKey, onPick, inr, annualPrice, isExpired }) {
  const displayPrice = cycle === "annual" ? annualPrice(tier.monthly_price) : tier.monthly_price;
  const perPeriod = cycle === "annual" ? "/year" : "/month";
  const effectiveMonthly = cycle === "annual" ? Math.round(displayPrice / 12) : null;

  // iter-67k (Area 5) — When the user's current plan is expired we
  // must show a working Renew button on that tier's card instead of
  // the disabled "Current plan" pill.
  const showRenew = isExpired && isCurrent;

  const ctaLabel = () => {
    if (showRenew) return "Renew plan";
    // "Current plan" (disabled) only when tier AND cycle match — otherwise
    // the user can still switch cycle even on their existing tier.
    if (isCurrentCycle) return "Current plan";
    if (currentTierKey) {
      const order = ["starter", "professional", "growth"];
      const cur = order.indexOf(currentTierKey);
      const tgt = order.indexOf(tierKey);
      if (cur >= 0 && tgt >= 0) {
        if (tgt === cur) return "Switch cycle";
        return tgt > cur ? "Upgrade" : "Switch (downgrade)";
      }
    }
    return "Subscribe";
  };

  const btnDisabled = isCurrentCycle && !showRenew;

  return (
    <Card
      className={
        "relative flex flex-col " +
        (isCurrent ? "border-2 border-emerald-500 bg-emerald-50/40 shadow-md " : "") +
        (!isCurrent && isHighlighted ? "border-primary shadow-md " : "")
      }
      data-testid={`sub-tier-${tierKey}`}
    >
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold flex items-center gap-1" data-testid={`sub-tier-current-${tierKey}`}>
          <SealCheck size={11} weight="fill" /> Current plan
        </div>
      )}
      {!isCurrent && isHighlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center gap-1">
          <Star size={10} weight="fill" /> Recommended
        </div>
      )}
      <CardContent className="p-5 flex flex-col flex-1 gap-3">
        <div>
          <div className="overline">{tier.tier_name}</div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="font-display text-3xl font-bold tabular-nums">{inr(displayPrice)}</span>
            <span className="text-xs text-muted-foreground">{perPeriod}</span>
          </div>
          {cycle === "annual" && effectiveMonthly !== null && (
            <div className="text-[11px] text-emerald-700 font-medium mt-0.5">
              Effective {inr(effectiveMonthly)}/mo · save {ANNUAL_DISCOUNT_PCT}%
            </div>
          )}
        </div>
        <ul className="text-xs space-y-1.5 flex-1">
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {tier.student_limit === -1 ? "Unlimited" : tier.student_limit.toLocaleString()} students
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {tier.user_limit === -1 ? "Unlimited" : tier.user_limit} team members
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {tier.branch_limit === -1 ? "Unlimited" : `${tier.branch_limit} branch${tier.branch_limit === 1 ? "" : "es"}`}
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {tier.storage_gb_limit === -1 ? "Unlimited" : `${tier.storage_gb_limit} GB`} storage
          </li>
          {(tier.features || []).map(f => (
            <li key={f} className="flex items-center gap-1.5">
              <CheckCircle size={12} weight="duotone" className="text-primary" />
              {f.replace(/_/g, " ")}
            </li>
          ))}
        </ul>
        <Button
          variant={btnDisabled ? "outline" : (isHighlighted || showRenew ? "default" : "outline")}
          onClick={onPick}
          disabled={btnDisabled}
          data-testid={`sub-tier-cta-${tierKey}`}
          className="gap-1.5"
        >
          {ctaLabel()}
          {!btnDisabled && <ArrowRight size={13} />}
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Trial card — non-purchasable, shown for context so the user
// can see where they sit in the ladder.
// ============================================================
function TrialCard({ preset, isCurrent, daysLeft }) {
  return (
    <Card
      className={"relative flex flex-col " + (isCurrent ? "border-2 border-amber-500 bg-amber-50/40" : "")}
      data-testid="sub-tier-trial"
    >
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-semibold flex items-center gap-1">
          Current
        </div>
      )}
      <CardContent className="p-5 flex flex-col flex-1 gap-3">
        <div>
          <div className="overline">{preset?.tier_name || "Trial"}</div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="font-display text-3xl font-bold tabular-nums">Free</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {isCurrent && typeof daysLeft === "number"
              ? (daysLeft >= 0 ? `${daysLeft}d left` : `Expired ${Math.abs(daysLeft)}d ago`)
              : "14-day trial — no credit card"}
          </div>
        </div>
        <ul className="text-xs space-y-1.5 flex-1">
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {preset?.student_limit === -1 ? "Unlimited" : (preset?.student_limit ?? 10)} students
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {preset?.user_limit ?? 3} team members
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" />
            {preset?.storage_gb_limit ?? 1} GB storage
          </li>
        </ul>
        <Button variant="outline" disabled className="gap-1.5">
          {isCurrent ? "Current plan" : "Trial"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Enterprise card — no price shown; opens the Talk-to-Us dialog.
// ============================================================
function EnterpriseCard({ onEnquire }) {
  return (
    <Card className="relative flex flex-col border-primary/30" data-testid="sub-tier-enterprise">
      <CardContent className="p-5 flex flex-col flex-1 gap-3">
        <div>
          <div className="overline">Enterprise</div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="font-display text-2xl font-bold tabular-nums">Talk to us</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Custom limits, dedicated support, SLAs
          </div>
        </div>
        <ul className="text-xs space-y-1.5 flex-1">
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" /> Unlimited students & users
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" /> Priority support & onboarding
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" /> Custom SLAs
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle size={12} weight="duotone" className="text-primary" /> API access & white-label
          </li>
        </ul>
        <Button onClick={onEnquire} className="gap-1.5" data-testid="enterprise-enquire-btn">
          Get in touch <ArrowRight size={13} />
        </Button>
      </CardContent>
    </Card>
  );
}

